// src/components/apps/SharePointComs/SharePointComsPage.jsx
import React, { useState } from "react";
import { Box, Typography, Paper, Button, Tabs, Tab } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CampaignIcon from "@mui/icons-material/Campaign";
import PolicyIcon from "@mui/icons-material/Policy";
import SharePointComsFileBrowser from "./SharePointComsFileBrowser";
import SharePointComsUploadDialog from "./SharePointComsUploadDialog";
import { useAuth } from "../../../context/AuthContext";

const TABS = [
  { label: "Communications", folder: "Communications", icon: <CampaignIcon /> },
  { label: "Policies", folder: "Policies", icon: <PolicyIcon /> },
];

const SharePointComsPage = () => {
  const { isSso } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);

  if (!isSso) {
    return (
      <Box sx={{ mt: 6, textAlign: "center" }}>
        <Typography variant="h5">
          SSO login is required to access Communications & Policies.
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          Please log in with your Microsoft account to access this feature.
        </Typography>
      </Box>
    );
  }

  const handleRefresh = () => setRefresh((prev) => !prev);
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setCurrentFolderId(null);
  };

  const currentTab = TABS[activeTab];

  return (
    <Box component="main" sx={{ mt: 4 }}>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          padding: 3,
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          marginTop: "50px",
          marginBottom: 4,
          textAlign: "center",
          transition: "transform 0.3s ease, boxShadow 0.3s ease",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, color: "primary.main" }}>
          Communications & Policies
        </Typography>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { fontSize: "1.1rem", fontWeight: 500, py: 2 },
            }}
          >
            {TABS.map((tab, idx) => (
              <Tab key={idx} icon={tab.icon} iconPosition="start" label={tab.label} />
            ))}
          </Tabs>
        </Paper>

        {/* Actions */}
        <Paper sx={{ padding: 3, mb: 4, display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Upload File
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Paper>

        {/* File Browser — keyed by folder so it remounts on tab switch */}
        <Paper sx={{ padding: 3, textAlign: "left" }}>
          <SharePointComsFileBrowser
            key={currentTab.folder}
            refresh={refresh}
            rootFolder={currentTab.folder}
            rootLabel={currentTab.label}
            onFolderChange={setCurrentFolderId}
          />
        </Paper>
      </Box>

      <SharePointComsUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        parentFolderId={currentFolderId}
        onUploadComplete={handleRefresh}
      />
    </Box>
  );
};

export default SharePointComsPage;